import { computed } from 'vue';

/* ---------------- HELPERS ---------------- */

function calculateAge(birthDate: string): number | null {
  if (!birthDate) return null;

  const today = new Date();
  const birth = new Date(birthDate);

  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}

function validateScore2Parameters(age: number): {
  isValid: boolean;
  adjustedAge?: number;
  message?: string;
} {
  if (age > 69) {
    return { isValid: false, message: "l'âge doit être < 70 ans" };
  }
  if (age < 40) {
    return { isValid: true, adjustedAge: 40, message: 'Age ajusté à 40 ans' };
  }
  return { isValid: true };
}

function getNonHDLCol(v: number): number {
  if (v < 150) return 0;
  if (v <= 200) return 1;
  if (v <= 250) return 2;
  return 3;
}

function getTensionRow(v: number): number {
  if (v >= 160) return 0;
  if (v >= 140) return 1;
  if (v >= 120) return 2;
  return 3;
}

function getAgeRange(age: number): string {
  if (age >= 65) return '65-69';
  if (age >= 60) return '60-64';
  if (age >= 55) return '55-59';
  if (age >= 50) return '50-54';
  if (age >= 45) return '45-49';
  return '40-44';
}

/* ---------------- TABLE ---------------- */

type ScoreTable = Record<string, Record<string, Record<string, number[][]>>>;

const scoreTable: ScoreTable = {
  F: {
    false: {
      '65-69': [
        [8, 8, 9, 9],
        [7, 7, 7, 7],
        [5, 6, 6, 6],
        [5, 5, 5, 5],
      ],
      '60-64': [
        [6, 6, 7, 7],
        [5, 5, 5, 6],
        [4, 4, 4, 5],
        [3, 3, 4, 4],
      ],
      '55-59': [
        [4, 5, 5, 5],
        [3, 4, 4, 4],
        [3, 3, 3, 3],
        [2, 2, 3, 3],
      ],
      '50-54': [
        [3, 4, 4, 4],
        [3, 3, 3, 3],
        [2, 2, 2, 3],
        [2, 2, 2, 2],
      ],
      '45-49': [
        [2, 3, 3, 3],
        [2, 2, 2, 3],
        [1, 2, 2, 2],
        [1, 1, 1, 1],
      ],
      '40-44': [
        [2, 2, 2, 3],
        [1, 2, 2, 2],
        [1, 1, 1, 1],
        [1, 1, 1, 1],
      ],
    },
    true: {
      '65-69': [
        [12, 12, 13, 13],
        [10, 10, 11, 11],
        [8, 9, 9, 9],
        [7, 7, 7, 8],
      ],
      '60-64': [
        [10, 10, 11, 11],
        [8, 8, 9, 9],
        [6, 7, 7, 8],
        [5, 6, 6, 6],
      ],
      '55-59': [
        [8, 8, 9, 10],
        [6, 7, 7, 8],
        [5, 6, 6, 6],
        [4, 4, 5, 5],
      ],
      '50-54': [
        [6, 7, 7, 8],
        [5, 5, 6, 6],
        [4, 4, 5, 5],
        [3, 3, 4, 4],
      ],
      '45-49': [
        [5, 5, 6, 7],
        [4, 4, 5, 5],
        [3, 3, 4, 4],
        [2, 2, 3, 3],
      ],
      '40-44': [
        [4, 4, 5, 6],
        [3, 3, 4, 4],
        [2, 3, 3, 3],
        [2, 2, 2, 2],
      ],
    },
  },
  M: {
    false: {
      '65-69': [
        [11, 12, 12, 13],
        [9, 10, 11, 11],
        [8, 8, 9, 10],
        [6, 7, 7, 8],
      ],
      '60-64': [
        [8, 9, 10, 11],
        [7, 8, 8, 9],
        [6, 6, 7, 8],
        [5, 5, 6, 6],
      ],
      '55-59': [
        [7, 7, 8, 9],
        [5, 6, 7, 8],
        [4, 5, 5, 6],
        [4, 4, 4, 5],
      ],
      '50-54': [
        [5, 6, 7, 8],
        [4, 5, 5, 6],
        [3, 4, 4, 5],
        [3, 3, 3, 4],
      ],
      '45-49': [
        [4, 5, 6, 6],
        [3, 4, 4, 5],
        [2, 3, 3, 4],
        [2, 2, 3, 3],
      ],
      '40-44': [
        [3, 4, 5, 5],
        [2, 3, 3, 4],
        [2, 2, 3, 3],
        [1, 2, 2, 2],
      ],
    },
    true: {
      '65-69': [
        [15, 16, 17, 19],
        [13, 14, 15, 16],
        [11, 12, 13, 13],
        [9, 10, 11, 11],
      ],
      '60-64': [
        [13, 14, 15, 17],
        [10, 11, 13, 14],
        [9, 10, 10, 11],
        [7, 8, 9, 10],
      ],
      '55-59': [
        [10, 12, 13, 15],
        [9, 10, 11, 12],
        [7, 8, 9, 10],
        [6, 6, 7, 8],
      ],
      '50-54': [
        [9, 10, 11, 13],
        [7, 8, 9, 10],
        [6, 6, 7, 8],
        [4, 5, 6, 7],
      ],
      '45-49': [
        [7, 8, 10, 11],
        [6, 7, 8, 9],
        [4, 5, 6, 7],
        [3, 4, 5, 5],
      ],
      '40-44': [
        [6, 7, 8, 10],
        [5, 5, 6, 8],
        [3, 4, 5, 6],
        [3, 3, 4, 5],
      ],
    },
  },
};

/* ---------------- CORE ---------------- */

function calculateScore2(
  gender: string,
  age: number,
  smoking: boolean | string,
  systolic: number,
  cholesterol: number,
): number | undefined {
  const smokingKey = smoking === true || smoking === 'true' ? 'true' : 'false';
  const ageRange = getAgeRange(age);
  const tensionRow = getTensionRow(systolic);
  const cholCol = getNonHDLCol(cholesterol);

  return scoreTable[gender]?.[smokingKey]?.[ageRange]?.[tensionRow]?.[cholCol];
}

function adjustScore(score: number | undefined, diabetes: boolean): number | null {
  if (score == null) return null;
  return diabetes && score < 5 ? 5 : score;
}

function getScoreColor(score: number | null, age: number): string {
  if (score == null) return 'medium';

  if ((age < 50 && score < 2.5) || (age >= 50 && age <= 69 && score < 5)) return 'success';
  if ((age < 50 && score < 7.5) || (age >= 50 && age <= 69 && score < 10)) return 'warning';

  return 'error';
}

/* =========================
   COMPOSABLE
========================= */

export function useScore2(coordinatorPatientMcvSend: any) {
  const coordinatorPatientMcv = computed(
    () => coordinatorPatientMcvSend?.value ?? coordinatorPatientMcvSend,
  );

  const gender = computed<string>(() =>
    coordinatorPatientMcv.value?.patient?.civility === 'F' ? 'F' : 'M',
  );

  const birthDate = computed<string | null>(() => {
    const p = coordinatorPatientMcv.value?.patient;
    if (!p?.birth_day || !p?.birth_month || !p?.birth_year) return null;

    return `${p.birth_year}-${String(p.birth_month).padStart(2, '0')}-${String(p.birth_day).padStart(2, '0')}`;
  });

  const age = computed<number | null>(() => {
    if (!birthDate.value) return null;
    return calculateAge(birthDate.value);
  });

  const validation = computed(() =>
    age.value != null ? validateScore2Parameters(age.value) : null,
  );

  const computedScore = computed<number | null>(() => {
    if (!coordinatorPatientMcv.value || !validation.value?.isValid) return null;

    const ageToUse = validation.value.adjustedAge ?? age.value!;

    const rawScore = calculateScore2(
      gender.value,
      ageToUse,
      coordinatorPatientMcv.value.smoking,
      coordinatorPatientMcv.value.systolic_blood_pressure,
      coordinatorPatientMcv.value.no_hdl_cholesterol,
    );

    return adjustScore(rawScore, coordinatorPatientMcv.value.diabetes);
  });

  const scoreColor = computed<string>(() => {
    if (!validation.value) return 'medium';
    const ageToUse = validation.value.adjustedAge ?? age.value!;
    return getScoreColor(computedScore.value, ageToUse);
  });

  return { age, score2: computedScore, scoreColor, validation };
}
